@if (loading() || store.saving()) {
  <maximizer-loader type="legacy"></maximizer-loader>
}

<form
  class="flex h-full min-h-0 min-w-0 flex-1 flex-col font-body w-full"
  [formGroup]="form"
  novalidate
>
  <header
    class="flex-shrink-0 border-b border-neutral-5 bg-neutral-5 px-4 py-3"
  >
    <div class="mx-auto flex max-w-[1065px] flex-row flex-wrap items-center gap-3 sm:flex-nowrap">
      <div class="flex gap-2">
        <img
          [src]="cdn + '/assets/images/outlook/outlook-logo.png'"
          alt="Microsoft Outlook"
          class="block h-7 w-7"
        />
        <img
          [src]="cdn + '/assets/images/logo.svg'"
          alt="Maximizer CRM"
          class="block h-7 w-7"
        />
      </div>
      <h1 class="m-0 min-w-0 text-[1.15rem] font-bold text-component-text">
        {{ 'outlookSync.hero.title' | translate }}
      </h1>
    </div>
  </header>

  <section
    class="mx-auto grid w-full max-w-[1065px] flex-shrink-0 grid-cols-1 items-center gap-3 border-b border-neutral-20 px-4 py-4 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]"
  >
    <article
      class="flex items-center gap-3 rounded border border-inserv-80 bg-inserv-5 px-4 py-3"
    >
      <img
        [src]="cdn + '/assets/images/outlook/outlook-logo.png'"
        class="block h-[26px] w-[26px]"
        alt="Outlook"
      />
      <div class="min-w-0">
        <p
          class="m-0 mt-0.5 truncate text-[0.95rem] font-semibold text-component-text"
          [title]="userSyncConfiguration()?.microsoftUserEmail ?? ''"
        >
          {{ userSyncConfiguration()?.microsoftUserEmail }}
        </p>
      </div>
    </article>
    <div
      class="flex flex-row items-center justify-center gap-2 text-neutral-70 md:flex-col md:gap-0.5"
      aria-hidden="true"
    >
      <span
        class="fa-icons icon-20"
        [class.icon-arrow-right-arrow-left]="
          activeTab() === 'contacts' || activeTab() === 'tasks'
        "
        [class.icon-arrow-right]="
          activeTab() === 'emails' ||
          activeTab() === 'appointments'
        "
      ></span>
    </div>
    <article
      class="flex items-center gap-3 rounded border border-primary-100 bg-primary-10 px-4 py-3"
    >
      <img
        [src]="cdn + '/assets/images/logo.svg'"
        alt="Maximizer account"
        class="block h-[26px] w-[26px]"
      />
      <div class="min-w-0">
        <p
          class="m-0 mt-0.5 truncate text-[0.95rem] font-semibold text-component-text"
          [title]="userSyncConfiguration()?.maximizerUserEmail || sessionEmail()"
        >
          {{ userSyncConfiguration()?.maximizerUserEmail || sessionEmail() }}
        </p>
      </div>
    </article>
  </section>

  @if (showConnectionWarning) {
    <section class="mx-auto flex w-full max-w-[1065px] flex-shrink-0 flex-col gap-2 px-4 pt-3">
      <article
        class="flex flex-wrap items-center gap-3 border border-error-10 bg-error-0 px-3 py-3 text-sm text-error-140"
      >
        <span>
          <strong>{{
            'outlookSync.alerts.connectionTitle' | translate
          }}</strong>
          {{
            'outlookSync.alerts.connectionDescription'
              | translate
          }}
        </span>
        <button
          id="outlook-sync-ui-configuration-reauthenticate"
          kendoButton
          themeColor="primary"
          [disabled]="reauthenticating()"
          (click)="reauthenticate()"
        >
          {{
            'outlookSync.alerts.reauthenticateButton' | translate
          }}
        </button>
      </article>
    </section>
  }

  @if (showPermissionWarning) {
    <section class="mx-auto flex w-full max-w-[1065px] flex-shrink-0 flex-col gap-2 px-4 pt-3">
      <article
        class="border border-error-10 bg-error-0 px-3 py-3 text-sm text-error-140"
      >
        <strong>{{ 'outlookSync.alerts.permissionTitle' | translate }}</strong>
        {{ 'outlookSync.alerts.permissionDescription' | translate }}
      </article>
    </section>
  }

  <section
    class="sync-tabs mx-auto flex w-full min-h-0 max-w-[1065px] flex-1 flex-col px-4"
  >
    <kendo-tabstrip
      class="flex min-h-0 flex-1 flex-col"
      (tabSelect)="onTabSelect($event)"
    >
      @for (tab of tabs; track tab.tab; let i = $index) {
        <kendo-tabstrip-tab [selected]="selectedTabIndex() === i">
          <ng-template kendoTabTitle>
            <span
              class="inline-flex items-center gap-1.5 text-base font-semibold tracking-[0.01em]"
            >
              <span
                class="inline-flex h-[1.1rem] w-[1.1rem] items-center justify-center text-component-text"
                [class.text-bush-green-120]="getSyncControl(tab.tab).value"
                aria-hidden="true"
              >
                @switch (getTabIconKind(tab)) {
                  @case ('success') {
                    <span
                      class="fa-icons icon-12 icon-check text-grass-green-120"
                    ></span>
                  }
                  @case ('syncing') {
                    <span
                      class="fa-icons icon-12 icon-arrows-rotate animate-spin text-finserv-100"
                    ></span>
                  }
                  @case ('error') {
                    <span
                      class="fa-icons icon-12 icon-xmark text-error-120"
                    ></span>
                  }
                  @case ('waiting') {
                    <span class="fa-icons icon-12 icon-clock"></span>
                  }
                  @default {
                    <span class="fa-icons icon-12 icon-circle-pause"></span>
                  }
                }
              </span>
              {{ tab.labelKey | translate }}
            </span>
          </ng-template>
          <ng-template kendoTabContent>
            @switch (tab.tab) {
                @case ('emails') {
                  <maximizer-outlook-email-tab
                    [configuration]="userSyncConfiguration()"
                    [form]="form"
                  ></maximizer-outlook-email-tab>
                }
                @case ('contacts') {
                  <maximizer-outlook-contact-tab
                    [configuration]="userSyncConfiguration()"
                    [form]="form"
                    [payload]="payload()"
                  ></maximizer-outlook-contact-tab>
                }
                @case ('appointments') {
                  <maximizer-outlook-calendar-tab
                    [configuration]="userSyncConfiguration()"
                    [form]="form"
                    [payload]="payload()"
                  ></maximizer-outlook-calendar-tab>
                }
                @case ('tasks') {
                  <maximizer-outlook-task-tab
                    [configuration]="userSyncConfiguration()"
                    [form]="form"
                    [payload]="payload()"
                  ></maximizer-outlook-task-tab>
                }
              }
          </ng-template>
        </kendo-tabstrip-tab>
      }
    </kendo-tabstrip>
  </section>

  <footer
    class="sticky bottom-0 z-10 mt-auto w-full flex-shrink-0 border-t border-neutral-20 bg-white py-3"
  >
    <div class="mx-auto flex w-full max-w-[1065px] items-center justify-end gap-3 px-4">
      <button
        id="outlook-sync-ui-configuration-cancel"
        kendoButton
        type="button"
        class="uppercase w-24"
        (click)="close()"
      >
        {{ 'outlookSync.dialogs.close' | translate }}
      </button>
      <button
        id="outlook-sync-ui-configuration-save"
        kendoButton
        themeColor="primary"
        type="button"
        class="uppercase w-40"
        [disabled]="form.invalid || form.pristine || store.saving() || store.configLoadFailed() || showConnectionWarning"
        (click)="saveConfiguration()"
      >
        {{
          store.saving()
            ? ('outlookSync.actions.saving' | translate)
            : ('outlookSync.actions.saveChanges' | translate)
        }}
      </button>
    </div>
  </footer>
</form>

<div #notificationContainer class="contents"></div>

<maximizer-notification
  #successToast
  icon="check"
  type="success"
  animation="fade"
  [hideAfter]="3000"
  [closable]="true"
  horizontal="center"
  vertical="bottom"
  [appendTo]="notificationContainerRef()"
>
  {{ successToastMessage() }}
</maximizer-notification>

<maximizer-notification
  #errorToast
  icon="triangle-exclamation"
  type="error"
  animation="fade"
  [hideAfter]="5000"
  [closable]="true"
  horizontal="center"
  vertical="bottom"
  [appendTo]="notificationContainerRef()"
>
  {{ errorToastMessage() }}
</maximizer-notification>
